import { useMutation } from '@apollo/client';
import { EWorldMapBookingConfigurationFieldType } from '__generated__/globalTypes';
import { getBookingStringTranslation } from 'components/detail/view/DetailBooking/DetailBookingTranslations';
import { MainButton } from 'elements/button/MainButton';
import { S_TextNumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextNumberValue/TextNumberValue';
import {
    CreateBooking,
    CreateBookingVariables
} from 'graphql/mutation/__generated__/CreateBooking';
import { CREATE_BOOKING_MUTATION } from 'graphql/mutation/worldMutation';
import {
    BookingQueryView_worldMarkerBookingConfiguration,
    BookingQueryView_worldMarkerBookingConfiguration_fields
} from 'graphql/query/__generated__/BookingQueryView';
import { EFonts } from 'libs/styles/styling';
import { VALID_EMAIL_HASH } from 'libs/utils/validation/validation';
import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { EWorldColor } from 'static/config/worldVariables';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    worldMapId: string;
    worldMapMarkerId: string;
    bookingConfig: BookingQueryView_worldMarkerBookingConfiguration;
    onSubmit: (email: string) => void;
    onError: () => void;
}

export const DetailBookingForm = (props: Props): JSX.Element => {
    const language = Localization.getLanguage();

    const [values, setValues] = useState(
        Object.fromEntries(
            props.bookingConfig.fields
                .map<[string, unknown]>((field) => {
                    if (field.type === EWorldMapBookingConfigurationFieldType.STRING) {
                        return [field.fieldId, ''];
                    }
                    if (field.type === EWorldMapBookingConfigurationFieldType.BOOLEAN) {
                        return [field.fieldId, false];
                    }
                    return null;
                })
                .filter((e) => e !== null)
        )
    );

    const [blurredFieldIds, setBlurredFieldIds] = useState<string[]>([]);
    const [visibleErrors, setVisibleErrors] = useState<[string, string][]>([]);
    const [canSubmit, setCanSubmit] = useState(false);

    const [sendSubmitMutation, submitMutation] = useMutation<CreateBooking, CreateBookingVariables>(
        CREATE_BOOKING_MUTATION
    );

    // This method is 1:1 repliacted on both FE and BE. Change it always in both places!
    function getFieldError(
        field: BookingQueryView_worldMarkerBookingConfiguration_fields,
        _value: string | boolean
    ): string | null {
        if (field.validation?.required) {
            if (
                field.type === EWorldMapBookingConfigurationFieldType.EMAIL ||
                field.type === EWorldMapBookingConfigurationFieldType.STRING
            ) {
                const value = _value as string;
                if (!value || !value.trim()) {
                    return Localization.FIELD_REQUIRED;
                }
            }
        }

        if (field.type === EWorldMapBookingConfigurationFieldType.EMAIL) {
            const value = (_value as string).trim();
            const isInvalidEmail = !value.match(VALID_EMAIL_HASH);
            if (isInvalidEmail) {
                return Localization.INVALID_EMAIL;
            }
        }

        return null;
    }

    useEffect(() => {
        const allErrors = [];

        props.bookingConfig.fields.forEach((field) => {
            const value = values[field.fieldId] as string | boolean;
            const error = getFieldError(field, value);

            if (error) {
                allErrors.push([field.fieldId, error]);
            }
        });

        setVisibleErrors(allErrors.filter((e) => blurredFieldIds.includes(e[0])));
        setCanSubmit(allErrors.length === 0);
    }, [props.bookingConfig.fields, values, blurredFieldIds]);

    function submit() {
        const emailField = props.bookingConfig.fields.find(
            (e) => e.type === EWorldMapBookingConfigurationFieldType.EMAIL
        );

        const email = values[emailField.fieldId] as string;

        sendSubmitMutation({
            variables: {
                worldMapId: props.worldMapId,
                worldMapMarkerId: props.worldMapMarkerId,
                data: values
            },
            onCompleted: () => {
                props.onSubmit?.(email);
            },
            onError: () => {
                props.onError();
            }
        });
    }

    return (
        <S_DetailBookingForm className={props.className}>
            {props.bookingConfig.fields.map((field) => {
                const inputId = `input-${field.fieldId}`;
                const value = values[field.fieldId];
                const error = visibleErrors.find((e) => e[0] === field.fieldId)?.[1] ?? null;

                if (field.type === EWorldMapBookingConfigurationFieldType.HEADLINE) {
                    return (
                        <S_Headline key={field.fieldId}>
                            {getBookingStringTranslation(field.title, language)}
                        </S_Headline>
                    );
                }
                if (
                    field.type === EWorldMapBookingConfigurationFieldType.STRING ||
                    field.type === EWorldMapBookingConfigurationFieldType.EMAIL
                ) {
                    return (
                        <S_InputRow key={field.fieldId}>
                            <S_InputFieldLabel htmlFor={inputId}>
                                {getBookingStringTranslation(field.title, language)}
                                {field.validation?.required && <S_StarRequired>*</S_StarRequired>}
                            </S_InputFieldLabel>
                            <S_InputFieldContainer>
                                <S_InputField
                                    id={inputId}
                                    value={value as string}
                                    type={
                                        field.type === EWorldMapBookingConfigurationFieldType.EMAIL
                                            ? 'email'
                                            : 'text'
                                    }
                                    onChange={(e) => {
                                        setValues((curr) => ({
                                            ...curr,
                                            [field.fieldId]: e.target.value
                                        }));
                                    }}
                                    onBlur={() => {
                                        setBlurredFieldIds(
                                            uniq([...blurredFieldIds, field.fieldId])
                                        );
                                    }}
                                />
                                {error && <S_InputFieldError>{error}</S_InputFieldError>}
                            </S_InputFieldContainer>
                        </S_InputRow>
                    );
                }

                if (field.type === EWorldMapBookingConfigurationFieldType.BOOLEAN) {
                    return (
                        <S_CheckboxRow key={field.fieldId}>
                            <S_CheckboxField
                                id={inputId}
                                type="checkbox"
                                checked={value as boolean}
                                onChange={(e) => {
                                    setValues((curr) => ({
                                        ...curr,
                                        [field.fieldId]: e.target.checked
                                    }));
                                }}
                            />
                            <S_CheckboxLabel htmlFor={inputId}>
                                {getBookingStringTranslation(field.title, language)}
                            </S_CheckboxLabel>
                        </S_CheckboxRow>
                    );
                }
            })}

            <S_SubmitButton
                type={'primary'}
                iconName={submitMutation.loading ? 'spinner' : null}
                disabled={!canSubmit || submitMutation.loading}
                onClick={submit}
                alignText="center"
            >
                {submitMutation.loading ? '' : Localization.BOOKING_SUBMIT}
            </S_SubmitButton>
        </S_DetailBookingForm>
    );
};

const S_DetailBookingForm = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Headline = styled.div`
    font-family: ${EFonts.Bold};
    margin-top: 18px;
    margin-bottom: 14px;
    &:first-child {
        margin-top: 0;
    }
`;

const S_InputRow = styled.div`
    margin-bottom: 22px;
    display: flex;
    flex-direction: column;
`;

const S_InputFieldContainer = styled.div`
    position: relative;
`;

const S_InputField = styled(S_TextNumberValue)`
    outline: none;
    border-top: none;
    border-left: none;
    border-right: none;
    box-sizing: border-box;
    padding: 0 10px;
    border-radius: 7px;
    font-size: 12px;
    color: #141c54;
    font-family: ${EFonts.Regular};
    width: ${(props) => (props.width ? props.width : '100%')};
    font-size: 12px;
    border-bottom: 1px solid #dadada;
    background: #f5f5f5;
    box-shadow: 0px 0px 20px 0px #dedede inset;
    height: 40px;
    text-align: left;
    &:focus {
        background: #f5f5f5;
    }
    &::placeholder {
        color: #909090;
    }
`;

const S_InputFieldLabel = styled.label`
    margin-bottom: 4px;
`;

const S_InputFieldError = styled.div`
    background: #${EWorldColor.RED};
    color: #fff;
    font-size: 11px;
    font-family: ${EFonts.Medium};
    position: absolute;
    bottom: -13px;
    left: 0;
    right: 0;
    padding: 3px 12px;
    border-radius: 0 0 7px 7px;
`;

const S_CheckboxRow = styled.div`
    margin-bottom: 14px;
    display: flex;
    flex-direction: row;
    align-items: start;
`;

const S_CheckboxField = styled.input`
    border-radius: 7px;
    background: #f5f5f5;
    box-shadow: 0px 0px 20px 0px #dedede inset;
    margin: 3px 6px 0 0;
    &:checked {
        box-shadow: none;
    }
`;

const S_CheckboxLabel = styled.label``;

const S_SubmitButton = styled(MainButton)`
    height: 50px;
    width: 300px;
    margin: 10px auto;
`;

const S_StarRequired = styled.span`
    color: #${EWorldColor.RED};
`;
